/**
 * Phase 293 — Payment Verified Dashboard Tile (academy owner)
 *
 * - When verified: renders a compact "Payment Infrastructure Verified ✓" pill.
 * - When NOT verified but credentials saved: nudges the owner to /settings to
 *   run the Health Check Wizard.
 * - When credentials are missing: silently hides (Phase 282d pattern).
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShieldCheck, AlertTriangle, Stethoscope } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PaymentVerifiedTile() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    api.get('/academy/razorpay/health-status')
      .then((r) => { if (alive) { setData(r.data || null); setLoaded(true); } })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  if (!loaded) return null;
  if (!data?.razorpay_configured) return null; // silently hide pre-config

  const verified = !!data.razorpay_health?.verified_at;
  const lastChecked = data.razorpay_health?.last_checked_at;
  const failureReason = data.razorpay_health?.last_failure_reason;

  if (verified) {
    return (
      <Card
        className="border-emerald-200 dark:border-emerald-900 bg-gradient-to-r from-emerald-50 to-white dark:from-emerald-950/40 dark:to-slate-900"
        data-testid="payment-verified-tile"
      >
        <CardContent className="p-3 flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">
              Payment infrastructure verified ✓
            </p>
            {lastChecked && (
              <p className="text-[11px] text-emerald-700 dark:text-emerald-400">
                Verified · last checked {new Date(lastChecked).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-emerald-700 hover:text-emerald-800 text-xs"
            onClick={() => navigate('/settings')}
            data-testid="payment-verified-tile-recheck"
          >
            Re-check
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Configured but not verified yet — nudge owner to run the wizard
  return (
    <Card
      className="border-amber-200 dark:border-amber-900 bg-gradient-to-r from-amber-50 to-white dark:from-amber-950/40 dark:to-slate-900"
      data-testid="payment-verified-tile-nudge"
    >
      <CardContent className="p-3 flex items-center gap-3 flex-wrap">
        <div className="h-9 w-9 rounded-lg bg-amber-100 dark:bg-amber-950 text-amber-700 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
            Payment setup not verified yet
          </p>
          <p className="text-[11px] text-amber-700 dark:text-amber-400 leading-snug">
            {failureReason
              ? `Last check failed: ${failureReason.slice(0, 120)}${failureReason.length > 120 ? '…' : ''}`
              : 'Run the 3-step Health Check Wizard before going live with fees.'}
          </p>
        </div>
        <Button
          size="sm"
          className="bg-amber-600 hover:bg-amber-700 text-white"
          onClick={() => navigate('/settings')}
          data-testid="payment-verified-tile-run-wizard-btn"
        >
          <Stethoscope className="h-3.5 w-3.5 mr-1" />
          Run Health Check
        </Button>
      </CardContent>
    </Card>
  );
}
